import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  HeartPulse,
  Settings,
  Bell,
  Globe,
  LogOut,
  ChevronsUpDown,
  Check,
  Plus,
  BadgeCheck,
  AudioWaveform,
  EllipsisVertical,
  Building2,
  AlertCircle,
  Sun,
  Moon,
  Monitor,
} from "lucide-react";
import { useTheme } from "next-themes";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useProjectStore } from "@/lib/project-store";
import { useOrganizationStore } from "@/lib/organization-store";
import {
  getProjects,
  postProjects,
  getAuthMe,
  getOrganizations,
  postOrganizations,
  getProjectMonitors,
  getProjectIncidents,
} from "@/api/project-api";
import "@/api/client";
import { useProjectIncidentEvents } from "@/hooks/use-incident-events";
import { OnboardingChecklist } from "@/components/onboarding-checklist";

const navItems = [
  { title: "Monitors", icon: Activity, path: "", absolute: false },
  {
    title: "Heartbeats",
    icon: HeartPulse,
    path: "/heartbeats",
    absolute: false,
  },
  { title: "Incidents", icon: Bell, path: "/incidents", absolute: false },
  {
    title: "Status Pages",
    icon: Globe,
    path: "/status-pages",
    absolute: false,
  },
];

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const { currentProjectId: storedProjectId, setCurrentProjectId } =
    useProjectStore();
  const {
    currentOrganizationId,
    setCurrentOrganizationId,
    orgIncidentStatus,
    setOrgIncidentStatus,
  } = useOrganizationStore();
  const effectiveProjectId = projectId ?? storedProjectId ?? "";
  const { isMobile } = useSidebar();
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();

  useProjectIncidentEvents();

  const [selectorOpen, setSelectorOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newOrgName, setNewOrgName] = useState("");
  const [creating, setCreating] = useState(false);
  const [creatingOrg, setCreatingOrg] = useState(false);

  const { data: meData } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const res = await getAuthMe();
      if (res.error) throw new Error("Failed to fetch user");
      return res.data;
    },
  });

  const user = meData?.data;
  const userName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.email?.split("@")[0] ||
    "User";
  const userEmail = user?.email || "";
  const userInitials = userName.slice(0, 2).toUpperCase();

  // Seed org store from /auth/me response
  useEffect(() => {
    if (user?.organizationId && !currentOrganizationId) {
      setCurrentOrganizationId(user.organizationId);
    }
  }, [user?.organizationId, currentOrganizationId, setCurrentOrganizationId]);

  const { data: orgsData } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const res = await getOrganizations();
      if (res.error) throw new Error("Failed to fetch organizations");
      return res.data;
    },
  });

  const orgs = orgsData?.data ?? [];
  const currentOrg =
    orgs.find((o) => o.id === currentOrganizationId) ??
    orgs.find((o) => o.id === user?.organizationId);

  const { data: projectsData } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const res = await getProjects();
      if (res.error) throw new Error("Failed to fetch projects");
      return res.data;
    },
  });

  const projects = projectsData?.data ?? [];
  const currentProject = projects.find((p) => p.id === effectiveProjectId);

  const projectStatsQueries = useQueries({
    queries: projects.map((p) => ({
      queryKey: ["project-stats", p.id],
      queryFn: async () => {
        const [monitorsRes, incidentsRes] = await Promise.all([
          getProjectMonitors({
            path: { projectId: p.id },
            query: { limit: 1 },
          }),
          getProjectIncidents({
            path: { projectId: p.id },
            query: { status: "open", limit: 1 },
          }),
        ]);
        return {
          monitorCount: monitorsRes.data?.pagination?.total ?? 0,
          openIncidentCount: incidentsRes.data?.pagination?.total ?? 0,
        };
      },
      staleTime: 30 * 1000,
      enabled: projects.length > 0,
    })),
  });

  const projectStatsMap = useMemo(
    () =>
      Object.fromEntries(
        projects.map((p, i) => [
          p.id,
          projectStatsQueries[i]?.data ?? {
            monitorCount: 0,
            openIncidentCount: 0,
          },
        ]),
      ),
    [projects, projectStatsQueries],
  );

  const hasAnyActiveIncident = useMemo(
    () =>
      projects.some((p) => (projectStatsMap[p.id]?.openIncidentCount ?? 0) > 0),
    [projects, projectStatsMap],
  );

  useEffect(() => {
    if (currentOrganizationId && projects.length > 0) {
      setOrgIncidentStatus(currentOrganizationId, hasAnyActiveIncident);
    }
  }, [
    currentOrganizationId,
    hasAnyActiveIncident,
    projects.length,
    setOrgIncidentStatus,
  ]);

  const handleOrgChange = (orgId: string) => {
    setCurrentOrganizationId(orgId);
    useProjectStore.getState().setCurrentProjectId(null as unknown as string);
    queryClient.removeQueries({ queryKey: ["projects"] });
    queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    navigate("/");
  };

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) return;
    setCreatingOrg(true);
    const res = await postOrganizations({ body: { name: newOrgName.trim() } });
    setCreatingOrg(false);

    if (res.error || !res.data) return;

    const newOrg = res.data.data;
    await queryClient.invalidateQueries({ queryKey: ["organizations"] });
    setNewOrgName("");
    setOrgDialogOpen(false);
    handleOrgChange(newOrg.id);
    toast.success(
      `Organization "${newOrg.name}" created. Switched to the new organization.`,
    );
  };

  const handleProjectChange = (newProjectId: string) => {
    setCurrentProjectId(newProjectId);
    setSelectorOpen(false);
    const currentPath = location.pathname;
    const projectPrefix = `/projects/${effectiveProjectId}`;
    const subPath = currentPath.startsWith(projectPrefix)
      ? currentPath.slice(projectPrefix.length)
      : "";
    navigate(`/projects/${newProjectId}${subPath}`);
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    setCreating(true);
    const res = await postProjects({ body: { name: newProjectName.trim() } });
    setCreating(false);

    if (res.error || !res.data) return;

    const newProject = res.data.data;
    await queryClient.invalidateQueries({ queryKey: ["projects"] });
    setNewProjectName("");
    setDialogOpen(false);
    setCurrentProjectId(newProject.id);
    navigate(`/projects/${newProject.id}`);
    toast.success(
      `Project "${newProject.name}" created. Switched to the new project.`,
    );
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("organization-preferences");
    useOrganizationStore.setState({
      currentOrganizationId: null,
      orgIncidentStatus: {},
    });
    queryClient.clear();
    navigate("/auth/login");
  };

  const projectBase = `/projects/${effectiveProjectId}`;
  const getNavPath = (suffix: string) => `${projectBase}${suffix}`;

  const isNavItemActive = (item: { path: string; absolute: boolean }) => {
    if (item.absolute) {
      return location.pathname.startsWith(item.path);
    }
    const fullPath = getNavPath(item.path);
    if (item.path === "") return location.pathname === fullPath;
    return location.pathname.startsWith(fullPath);
  };

  const getNavTarget = (item: { path: string; absolute: boolean }) => {
    if (item.absolute) return item.path;
    return getNavPath(item.path);
  };

  return (
    <>
      <Sidebar collapsible="icon">
        <SidebarHeader className="h-(--header-height) justify-center border-b p-0 gap-0">
          <SidebarMenu>
            <SidebarMenuItem className="group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <SidebarMenuButton
                      size="lg"
                      className="h-(--header-height) rounded-none px-4 group-data-[collapsible=icon]:rounded-md! group-data-[collapsible=icon]:px-1.5!"
                    />
                  }
                >
                  <img
                    src="/logo.svg"
                    alt="Watchplane"
                    className="!size-5 shrink-0"
                  />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {currentOrg?.name ?? "Organization"}
                    </span>
                  </div>
                  {hasAnyActiveIncident ||
                  Object.values(orgIncidentStatus).some(Boolean) ? (
                    <AlertCircle className="ml-auto size-4 shrink-0 text-destructive" />
                  ) : (
                    <ChevronsUpDown className="ml-auto size-4" />
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="min-w-56 rounded-lg"
                  align="start"
                  side="bottom"
                  sideOffset={4}
                >
                  <DropdownMenuGroup>
                    <DropdownMenuLabel className="p-0 font-normal">
                      <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                        <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                          <Building2 className="size-4" />
                        </div>
                        <div className="grid flex-1 text-left text-sm leading-tight">
                          <span className="truncate font-semibold">
                            {currentOrg?.name ?? "Organization"}
                          </span>
                          <span className="truncate text-xs text-muted-foreground capitalize">
                            {currentOrg?.role ?? ""}
                          </span>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      Settings
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => navigate("/settings/members")}
                    >
                      Members
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/settings/organization")}
                    >
                      Organization
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/settings/integrations")}
                    >
                      Integrations
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/settings/api-tokens")}
                    >
                      API Tokens
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/settings/billing")}
                    >
                      Billing
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      Organizations
                    </DropdownMenuLabel>
                    {orgs.map((org) => {
                      const isCurrentOrg = org.id === currentOrganizationId;
                      const orgHasIncident = isCurrentOrg
                        ? hasAnyActiveIncident
                        : (orgIncidentStatus[org.id] ?? false);
                      return (
                        <DropdownMenuItem
                          key={org.id}
                          onClick={() => handleOrgChange(org.id)}
                          className={cn(orgHasIncident && "text-destructive")}
                        >
                          {orgHasIncident && (
                            <AlertCircle className="size-4 shrink-0 text-destructive" />
                          )}
                          <span className="truncate">{org.name}</span>
                          <Check
                            className={cn(
                              "ml-auto size-4 shrink-0",
                              isCurrentOrg ? "opacity-100" : "opacity-0",
                            )}
                          />
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setOrgDialogOpen(true)}>
                    <Plus />
                    Create organization
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          {/* Project selector */}
          <SidebarGroup>
            <SidebarGroupLabel>Project</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <DropdownMenu
                    open={selectorOpen}
                    onOpenChange={setSelectorOpen}
                  >
                    <DropdownMenuTrigger
                      render={<SidebarMenuButton size="lg" />}
                    >
                      <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary/10 text-sidebar-primary">
                        <AudioWaveform className="size-4" />
                      </div>
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-semibold">
                          {currentProject?.name ?? "Select project"}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {projects.length} project
                          {projects.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <ChevronsUpDown className="ml-auto size-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="min-w-56 rounded-lg"
                      align="start"
                      side="bottom"
                      sideOffset={4}
                    >
                      <DropdownMenuGroup>
                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                          Projects
                        </DropdownMenuLabel>
                        {projects.map((project) => {
                          const stats = projectStatsMap[project.id];
                          const hasIncident =
                            (stats?.openIncidentCount ?? 0) > 0;
                          const isLoading =
                            projectStatsQueries[projects.indexOf(project)]
                              ?.isLoading;
                          return (
                            <DropdownMenuItem
                              key={project.id}
                              onClick={() => handleProjectChange(project.id)}
                              className="gap-2 p-2"
                            >
                              <div
                                className={cn(
                                  "flex size-6 items-center justify-center rounded-sm border shrink-0",
                                  hasIncident &&
                                    "border-destructive/50 bg-destructive/10",
                                )}
                              >
                                <AudioWaveform
                                  className={cn(
                                    "size-4 shrink-0",
                                    hasIncident && "text-destructive",
                                  )}
                                />
                              </div>
                              <div className="flex flex-col flex-1 min-w-0">
                                <span className="truncate text-sm">
                                  {project.name}
                                </span>
                                {!isLoading && (
                                  <span className="truncate text-xs text-muted-foreground">
                                    {stats?.monitorCount ?? 0}{" "}
                                    {(stats?.monitorCount ?? 0) === 1
                                      ? "monitor"
                                      : "monitors"}
                                    {hasIncident && (
                                      <span className="text-destructive">
                                        {" · "}
                                        {stats!.openIncidentCount}{" "}
                                        {stats!.openIncidentCount === 1
                                          ? "incident"
                                          : "incidents"}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </div>
                              {hasIncident && (
                                <span className="size-2 rounded-full bg-destructive shrink-0" />
                              )}
                              <Check
                                className={cn(
                                  "size-4 shrink-0",
                                  project.id === projectId
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuGroup>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectorOpen(false);
                          navigate(
                            `/projects/${effectiveProjectId}/project-settings`,
                          );
                        }}
                      >
                        <Settings />
                        Project settings
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="gap-2 p-2"
                        onClick={() => {
                          setSelectorOpen(false);
                          setDialogOpen(true);
                        }}
                      >
                        <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                          <Plus className="size-4" />
                        </div>
                        <div className="font-medium text-muted-foreground">
                          New project
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel>Navigation</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => {
                  const currentProjectStats =
                    projectStatsMap[effectiveProjectId];
                  const showIncidentBadge =
                    item.title === "Incidents" &&
                    (currentProjectStats?.openIncidentCount ?? 0) > 0;
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        isActive={isNavItemActive(item)}
                        onClick={() => navigate(getNavTarget(item))}
                        tooltip={item.title}
                      >
                        <item.icon />
                        <span>{item.title}</span>
                        {showIncidentBadge && (
                          <span className="ml-auto size-2 rounded-full bg-destructive shrink-0" />
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <OnboardingChecklist
            projectId={effectiveProjectId}
            monitorCount={
              projectStatsMap[effectiveProjectId]?.monitorCount ?? 0
            }
          />
        </SidebarContent>
        <SidebarFooter>
          <p className="px-2 text-xs text-muted-foreground/50 group-data-[collapsible=icon]:hidden">
            v{__APP_VERSION__}
          </p>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger render={<SidebarMenuButton />}>
                  {theme === "dark" ? (
                    <Moon className="size-4" />
                  ) : theme === "light" ? (
                    <Sun className="size-4" />
                  ) : (
                    <Monitor className="size-4" />
                  )}
                  <span className="flex-1 text-sm">
                    {theme === "dark"
                      ? "Dark"
                      : theme === "light"
                        ? "Light"
                        : "System"}
                  </span>
                  <ChevronsUpDown className="ml-auto size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="min-w-40 rounded-lg"
                  side={isMobile ? "bottom" : "right"}
                  align="end"
                  sideOffset={4}
                >
                  <DropdownMenuItem onClick={() => setTheme("light")}>
                    <Sun className="size-4" />
                    Light
                    {theme === "light" && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("dark")}>
                    <Moon className="size-4" />
                    Dark
                    {theme === "dark" && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("system")}>
                    <Monitor className="size-4" />
                    System
                    {(theme === "system" || !theme) && (
                      <Check className="ml-auto size-4" />
                    )}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <SidebarMenuButton
                      size="lg"
                      className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                    />
                  }
                >
                  <Avatar className="h-8 w-8 rounded-lg grayscale">
                    <AvatarFallback className="rounded-lg">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{userName}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {userEmail}
                    </span>
                  </div>
                  <EllipsisVertical className="ml-auto size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="min-w-56 rounded-lg"
                  side={isMobile ? "bottom" : "right"}
                  align="end"
                  sideOffset={4}
                >
                  <DropdownMenuGroup>
                    <DropdownMenuLabel className="p-0 font-normal">
                      <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                        <Avatar className="h-8 w-8 rounded-lg">
                          <AvatarFallback className="rounded-lg">
                            {userInitials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="grid flex-1 text-left text-sm leading-tight">
                          <span className="truncate font-medium">
                            {userName}
                          </span>
                          <span className="truncate text-xs text-muted-foreground">
                            {userEmail}
                          </span>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")}>
                    <BadgeCheck />
                    Account
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      {/* Create organization dialog */}
      <Dialog open={orgDialogOpen} onOpenChange={setOrgDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create new organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization name</Label>
              <Input
                id="org-name"
                placeholder="e.g. Acme Inc."
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateOrg();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrgDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateOrg}
              disabled={creatingOrg || !newOrgName.trim()}
            >
              {creatingOrg ? "Creating..." : "Create organization"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create project dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create new project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="project-name">Project name</Label>
              <Input
                id="project-name"
                placeholder="e.g. Production, Staging"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateProject();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={creating || !newProjectName.trim()}
            >
              {creating ? "Creating..." : "Create project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
